import { useId } from 'react';
import styles from './LineChart.module.css';

/**
 * The line chart the canvas draws by hand in SVG (1l), reproduced rather than replaced.
 *
 * The canvas specifies a `viewBox="0 0 560 150"` with three gridlines at y = 20, 75 and 130
 * running from x = 24 to x = 536. Those numbers are the chart: swapping in Recharts would
 * mean giving up the drawing that was approved and getting a different one back.
 *
 * Coordinates are in viewBox units and the SVG scales to its container, so the geometry
 * below is resolution-independent — no measuring, no ResizeObserver.
 */

const LEFT = 24;
const RIGHT = 536;
const TOP = 20;
const BOTTOM = 130;
const GRID_Y = [TOP, 75, BOTTOM];

export interface DotStyle {
  r: number;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface LineSeries {
  values: number[];
  /** A CSS colour — pass a var(--token), not a hex. */
  color: string;
  strokeWidth?: number;
  dashed?: boolean;
  /** Fills the area between the line and the floor of the plot. */
  area?: boolean;
  /** Per-point marker. Return null to leave a point unmarked. */
  dot?: (index: number) => DotStyle | null;
}

interface LineChartProps {
  series: LineSeries[];
  /** Fixed axis bounds. Omit either end to derive it from the data. */
  min?: number;
  max?: number;
  /** Required: an SVG with no accessible name is invisible to a screen reader. */
  ariaLabel: string;
}

/**
 * The plotted span. Padded by a tenth so a line never runs along the frame, and widened
 * when every value is identical — a flat series would otherwise divide by zero and the
 * whole chart would vanish.
 */
function domainOf(series: LineSeries[], min?: number, max?: number): [number, number] {
  const all = series.flatMap((s) => s.values);
  if (all.length === 0) return [0, 1];

  let lo = min ?? Math.min(...all);
  let hi = max ?? Math.max(...all);

  if (hi === lo) {
    lo -= 1;
    hi += 1;
  } else {
    const pad = (hi - lo) * 0.1;
    if (min == null) lo -= pad;
    if (max == null) hi += pad;
  }
  return [lo, hi];
}

export function LineChart({ series, min, max, ariaLabel }: LineChartProps) {
  const clipId = useId();
  const [lo, hi] = domainOf(series, min, max);
  const count = Math.max(...series.map((s) => s.values.length), 0);

  // A single point has no span to spread across, so it sits in the middle.
  const xAt = (i: number) =>
    count <= 1 ? (LEFT + RIGHT) / 2 : LEFT + ((RIGHT - LEFT) * i) / (count - 1);
  const yAt = (v: number) => BOTTOM - ((v - lo) / (hi - lo)) * (BOTTOM - TOP);

  const pointsOf = (values: number[]) =>
    values.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');

  // Scaling is uniform (default preserveAspectRatio, plus height:auto in CSS). Stretching the
  // box to fit instead would turn every dot into an ellipse and thin the strokes unevenly.
  return (
    <svg className={styles.svg} viewBox="0 0 560 150" role="img" aria-label={ariaLabel}>
      {/* Clipped so a value just outside a fixed axis range cannot draw over the labels. */}
      <clipPath id={clipId}>
        <rect x={LEFT - 8} y={TOP - 14} width={RIGHT - LEFT + 16} height={BOTTOM - TOP + 28} />
      </clipPath>

      {GRID_Y.map((y) => (
        <line key={y} x1={LEFT} y1={y} x2={RIGHT} y2={y} className={styles.grid} />
      ))}

      <g clipPath={`url(#${clipId})`}>
        {series.map((s, si) => {
          if (s.values.length === 0) return null;
          const points = pointsOf(s.values);
          return (
            <g key={si}>
              {s.area && s.values.length > 1 && (
                <polygon
                  points={`${xAt(0)},${BOTTOM} ${points} ${xAt(s.values.length - 1)},${BOTTOM}`}
                  fill={s.color}
                  fillOpacity={0.09}
                  stroke="none"
                />
              )}
              {s.values.length > 1 && (
                <polyline
                  points={points}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.strokeWidth ?? 2.4}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeDasharray={s.dashed ? '6 5' : undefined}
                />
              )}
              {s.dot &&
                s.values.map((v, i) => {
                  const style = s.dot?.(i);
                  if (!style) return null;
                  return (
                    <circle
                      key={i}
                      cx={xAt(i)}
                      cy={yAt(v)}
                      r={style.r}
                      fill={style.fill}
                      stroke={style.stroke}
                      strokeWidth={style.strokeWidth}
                    />
                  );
                })}
            </g>
          );
        })}
      </g>
    </svg>
  );
}
